import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Home, Palette, IndianRupee, Star, CheckCircle2, XCircle,
  ChevronRight, ArrowLeft, User, MapPin, ClipboardList, Wand2,
  Layers, Ruler, Plus, Send, Trash2, Lock, AlertTriangle, Snowflake, FileText,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtBudget = (n) => (n ? `₹${(n / 100000).toFixed(1)}L` : "—");

function ScoreRing({ score }) {
  const color = score >= 75 ? "#16a34a" : score >= 50 ? "#F56B1F" : "#9a9a9a";
  return (
    <div className="relative h-14 w-14 shrink-0" data-testid="designer-score-ring">
      <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E3DAC8" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${(score / 100) * 97.4} 97.4`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center mono text-sm font-bold text-neutral-800">{score}</div>
    </div>
  );
}

/* ---------------- Allocation (manager) ---------------- */
function Allocation({ onOpen }) {
  const [pending, setPending] = useState(null);
  const [recs, setRecs] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/designer/allocation/pending").then((r) => setPending(r.data)).catch(() => setPending([]));
  useEffect(() => { load(); }, []);

  const recommend = async (p) => {
    setActive(p); setRecs(null); setBusy(true);
    try { const { data } = await api.post(`/projects/${p.project.id}/allocation/recommend`); setRecs(data.recommendations); }
    catch { toast.error("Could not load recommendations"); }
    setBusy(false);
  };
  const assign = async (designerId, name) => {
    try {
      await api.post(`/projects/${active.project.id}/allocation/assign`, { designer_id: designerId });
      toast.success(`Offer sent to ${name}`);
      setActive(null); setRecs(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
  };

  if (!pending) return <div className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {pending.length === 0 && (
        <div className="bg-white border border-[#E3DAC8] rounded-2xl p-8 text-center text-sm text-neutral-500" data-testid="allocation-empty">
          No projects are currently waiting for a designer. Projects appear here after a customer requests a designer meeting.
        </div>
      )}
      {pending.map((row) => (
        <div key={row.project.id} data-testid={`allocation-row-${row.project.id}`} className="bg-white border border-[#E3DAC8] rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-heading font-bold text-neutral-900">{row.project.project_name}</div>
              <div className="text-xs mono text-neutral-400">{row.project.project_code}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-neutral-600">
                <span className="flex items-center gap-1"><User size={13} className="text-[#F56B1F]" /> {row.customer_name}</span>
                <span className="flex items-center gap-1"><Palette size={13} className="text-[#F56B1F]" /> {row.primary_style || "—"}</span>
                <span className="flex items-center gap-1"><IndianRupee size={13} className="text-[#F56B1F]" /> {fmtBudget(row.budget_range)}</span>
              </div>
              {row.offered_to && <div className="text-xs text-amber-700 mt-2">Offer pending with {row.offered_to}</div>}
            </div>
            <button onClick={() => recommend(row)} data-testid={`allocation-recommend-${row.project.id}`}
              className="shrink-0 flex items-center gap-2 rounded-xl bg-[#3E3E3E] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[#2a2a2a]">
              <Wand2 size={15} /> Recommend
            </button>
          </div>

          {active?.project.id === row.project.id && (
            <div className="mt-4 border-t border-[#E3DAC8] pt-4" data-testid="allocation-recommendations">
              <div className="text-xs uppercase tracking-wider text-neutral-400 font-semibold mb-3 flex items-center gap-2">
                <Sparkles size={13} className="text-[#F56B1F]" /> Intelligent recommendations {busy && <Loader2 size={13} className="animate-spin" />}
              </div>
              <div className="space-y-3">
                {recs?.map((d, i) => (
                  <div key={d.designer_id} data-testid={`recommendation-${d.designer_id}`}
                    className={`flex items-start gap-4 rounded-xl border p-4 ${i === 0 ? "border-[#F56B1F] bg-[#FDF0E7]" : "border-[#E3DAC8] bg-[#FAF8F3]"}`}>
                    <ScoreRing score={d.score} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-neutral-900">{d.designer_name}</span>
                        {i === 0 && <span className="text-[10px] uppercase tracking-wider bg-[#F56B1F] text-white px-1.5 py-0.5 rounded">Best fit</span>}
                        <span className="flex items-center gap-0.5 text-xs text-neutral-500 mono"><Star size={11} className="text-amber-500 fill-amber-500" />{d.rating}</span>
                      </div>
                      <ul className="mt-1.5 space-y-0.5">
                        {d.rationale.map((r, j) => <li key={j} className="text-xs text-neutral-600 flex items-start gap-1.5"><ChevronRight size={12} className="text-[#F56B1F] mt-0.5 shrink-0" />{r}</li>)}
                      </ul>
                    </div>
                    <button onClick={() => assign(d.designer_id, d.designer_name)} data-testid={`assign-${d.designer_id}`}
                      className="shrink-0 rounded-xl border border-[#F56B1F] text-[#D65A10] text-sm font-semibold px-4 py-2 hover:bg-[#F56B1F] hover:text-white">
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Designer's own assignments ---------------- */
function MyAssignments({ onOpen }) {
  const [data, setData] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/designer/workspace").then((r) => setData(r.data)).catch(() => setData({ offered: [], active: [] }));
  useEffect(() => { load(); }, []);

  const accept = async (aid) => { try { await api.post(`/designer/assignments/${aid}/accept`); toast.success("Project accepted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); } };
  const decline = async (aid) => {
    if (!reason.trim()) return toast.error("Please add a reason");
    try { await api.post(`/designer/assignments/${aid}/decline`, { reason }); toast.success("Declined"); setDeclining(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); }
  };

  if (!data) return <div className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6">
      {data.profile?.style_specializations && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-[#E3DAC8] rounded-xl p-4"><div className="text-2xl font-bold mono text-neutral-900">{data.stats.offered}</div><div className="text-xs text-neutral-500">New offers</div></div>
          <div className="bg-white border border-[#E3DAC8] rounded-xl p-4"><div className="text-2xl font-bold mono text-neutral-900">{data.stats.active}</div><div className="text-xs text-neutral-500">Active projects</div></div>
          <div className="bg-white border border-[#E3DAC8] rounded-xl p-4"><div className="text-2xl font-bold mono text-neutral-900">{data.stats.capacity ?? "—"}</div><div className="text-xs text-neutral-500">Capacity</div></div>
        </div>
      )}

      <div>
        <h3 className="font-heading font-bold text-neutral-900 mb-3">New offers</h3>
        {data.offered.length === 0 ? <div className="text-sm text-neutral-400">No pending offers.</div> : (
          <div className="space-y-3">
            {data.offered.map((a) => (
              <div key={a.id} data-testid={`offer-${a.id}`} className="bg-white border border-[#F56B1F] rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-neutral-900">{a.project_name}</div>
                    <div className="text-xs mono text-neutral-400">{a.project_code}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-neutral-600">
                      <span className="flex items-center gap-1"><User size={13} className="text-[#F56B1F]" /> {a.customer_name}</span>
                      <span className="flex items-center gap-1"><Palette size={13} className="text-[#F56B1F]" /> {a.primary_style || "—"}</span>
                      <span className="flex items-center gap-1"><IndianRupee size={13} className="text-[#F56B1F]" /> {fmtBudget(a.budget_range)}</span>
                    </div>
                    {a.rationale?.length > 0 && <div className="text-xs text-neutral-500 mt-2">Match score {a.score} — {a.rationale[0]}</div>}
                  </div>
                </div>
                {declining === a.id ? (
                  <div className="mt-3 space-y-2">
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`decline-reason-${a.id}`}
                      placeholder="Reason for declining…" className="w-full rounded-xl border border-[#E3DAC8] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" rows={2} />
                    <div className="flex gap-2">
                      <button onClick={() => decline(a.id)} data-testid={`confirm-decline-${a.id}`} className="rounded-xl bg-red-600 text-white text-sm font-semibold px-4 py-2">Confirm decline</button>
                      <button onClick={() => { setDeclining(null); setReason(""); }} className="rounded-xl border border-[#E3DAC8] text-sm px-4 py-2">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => accept(a.id)} data-testid={`accept-${a.id}`} className="flex items-center gap-1.5 rounded-xl bg-green-600 text-white text-sm font-semibold px-4 py-2"><CheckCircle2 size={15} /> Accept</button>
                    <button onClick={() => setDeclining(a.id)} data-testid={`decline-${a.id}`} className="flex items-center gap-1.5 rounded-xl border border-red-300 text-red-600 text-sm font-semibold px-4 py-2"><XCircle size={15} /> Decline</button>
                    <button onClick={() => onOpen(a.project_id)} className="ml-auto flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800">View details <ChevronRight size={15} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading font-bold text-neutral-900 mb-3">Active projects</h3>
        {data.active.length === 0 ? <div className="text-sm text-neutral-400">No active projects yet.</div> : (
          <div className="grid md:grid-cols-2 gap-3">
            {data.active.map((a) => (
              <button key={a.id} onClick={() => onOpen(a.project_id)} data-testid={`active-project-${a.project_id}`}
                className="text-left bg-white border border-[#E3DAC8] rounded-2xl p-5 hover:border-[#F56B1F] transition-colors">
                <div className="flex items-center justify-between"><span className="font-semibold text-neutral-900">{a.project_name}</span><ChevronRight size={16} className="text-neutral-400" /></div>
                <div className="text-xs mono text-neutral-400">{a.project_code}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-neutral-600">
                  <span className="flex items-center gap-1"><User size={13} className="text-[#F56B1F]" /> {a.customer_name}</span>
                  <span className="flex items-center gap-1"><Palette size={13} className="text-[#F56B1F]" /> {a.primary_style || "—"}</span>
                </div>
                <div className="mt-2 text-xs inline-block bg-[#EEE9DE] rounded-full px-2 py-0.5 text-neutral-600">{a.current_stage}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Consolidated project landing ---------------- */
const VSTATUS = {
  Draft: "bg-neutral-100 text-neutral-600", Submitted: "bg-blue-100 text-blue-700",
  "Under Review": "bg-blue-100 text-blue-700", "Changes Requested": "bg-amber-100 text-amber-700",
  Approved: "bg-green-100 text-green-700",
};

function VersionsPanel({ d, pid, reload }) {
  const frozen = d.design_frozen;
  const versions = d.versions || [];
  const roomTypes = d.concepts?.[0]?.estimate?.rooms?.map((r) => r.room_type) || ["Living Room", "Kitchen", "Master Bedroom"];
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", rooms: roomTypes.map((rt) => ({ room_type: rt, description: "", materials: "" })) });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Add a version title");
    setBusy(true);
    try { await api.post(`/designer/projects/${pid}/versions`, form); toast.success("Draft created"); setCreating(false); setForm({ title: "", summary: "", rooms: roomTypes.map((rt) => ({ room_type: rt, description: "", materials: "" })) }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    setBusy(false);
  };
  const submit = async (vid) => { try { await api.post(`/designer/versions/${vid}/submit`); toast.success("Sent to customer for review"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); } };
  const freeze = async () => { try { const { data } = await api.post(`/designer/projects/${pid}/design-freeze`); toast.success(`Design frozen (${data.frozen_version}) → handed to BOQ`); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not freeze"); } };
  const hasApproved = versions.some((v) => v.status === "Approved");

  return (
    <div className="space-y-4" data-testid="versions-panel">
      {frozen && (
        <div className="flex items-center gap-2 rounded-xl bg-[#3E3E3E] text-white px-4 py-3" data-testid="design-frozen-banner">
          <Snowflake size={18} className="text-[#7fd0ff]" />
          <span className="text-sm font-semibold">Design is frozen and handed off to BOQ. Versions are locked.</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-neutral-900">Design Versions</h3>
        <div className="flex gap-2">
          {!frozen && <button onClick={() => setCreating((c) => !c)} data-testid="new-version-btn" className="flex items-center gap-1.5 rounded-xl bg-[#3E3E3E] text-white text-sm font-semibold px-3 py-2"><Plus size={15} /> New version</button>}
          {!frozen && hasApproved && <button onClick={freeze} data-testid="design-freeze-btn" className="flex items-center gap-1.5 rounded-xl bg-[#F56B1F] text-white text-sm font-semibold px-3 py-2"><Snowflake size={15} /> Freeze design</button>}
        </div>
      </div>

      {creating && !frozen && (
        <div className="bg-white border border-[#E3DAC8] rounded-2xl p-5 space-y-3" data-testid="new-version-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Version title (e.g. Concept-aligned V1)" data-testid="version-title" className="w-full rounded-xl border border-[#E3DAC8] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
          <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Summary of this design direction…" rows={2} className="w-full rounded-xl border border-[#E3DAC8] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
          <div className="space-y-2">
            {form.rooms.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input value={r.room_type} onChange={(e) => { const rooms = [...form.rooms]; rooms[i] = { ...r, room_type: e.target.value }; setForm({ ...form, rooms }); }} className="col-span-3 rounded-lg border border-[#E3DAC8] px-2 py-2 text-sm" />
                <input value={r.description} onChange={(e) => { const rooms = [...form.rooms]; rooms[i] = { ...r, description: e.target.value }; setForm({ ...form, rooms }); }} placeholder="Design notes" className="col-span-5 rounded-lg border border-[#E3DAC8] px-2 py-2 text-sm" />
                <input value={r.materials} onChange={(e) => { const rooms = [...form.rooms]; rooms[i] = { ...r, materials: e.target.value }; setForm({ ...form, rooms }); }} placeholder="Materials" className="col-span-3 rounded-lg border border-[#E3DAC8] px-2 py-2 text-sm" />
                <button onClick={() => setForm({ ...form, rooms: form.rooms.filter((_, j) => j !== i) })} className="col-span-1 text-neutral-400 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            ))}
            <button onClick={() => setForm({ ...form, rooms: [...form.rooms, { room_type: "New room", description: "", materials: "" }] })} className="text-xs text-[#D65A10] font-semibold flex items-center gap-1"><Plus size={13} /> Add room</button>
          </div>
          <button onClick={create} disabled={busy} data-testid="save-version-btn" className="rounded-xl bg-[#F56B1F] text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-60">{busy ? "Saving…" : "Save draft"}</button>
        </div>
      )}

      {versions.length === 0 ? <div className="text-sm text-neutral-400">No design versions yet.</div> : versions.map((v) => (
        <div key={v.id} data-testid={`version-${v.version_label}`} className="bg-white border border-[#E3DAC8] rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FileText size={16} className="text-[#F56B1F]" /><span className="font-heading font-bold text-neutral-900">{v.version_label} · {v.title}</span></div>
            <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${VSTATUS[v.status] || "bg-neutral-100 text-neutral-600"}`}>{v.status}</span>
          </div>
          {v.summary && <p className="text-sm text-neutral-600 mt-1">{v.summary}</p>}
          {v.rooms?.length > 0 && <div className="mt-2 space-y-1">{v.rooms.map((r, i) => <div key={i} className="text-xs text-neutral-600"><b>{r.room_type}:</b> {r.description || "—"}{r.materials ? ` · ${r.materials}` : ""}</div>)}</div>}
          {v.customer_review?.comment && <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">Customer: {v.customer_review.comment}</div>}
          {!frozen && (v.status === "Draft" || v.status === "Changes Requested") && (
            <button onClick={() => submit(v.id)} data-testid={`submit-version-${v.version_label}`} className="mt-3 flex items-center gap-1.5 rounded-xl border border-[#F56B1F] text-[#D65A10] text-sm font-semibold px-4 py-2"><Send size={14} /> Send to customer</button>
          )}
        </div>
      ))}
    </div>
  );
}

function SitePanel({ d, pid, reload }) {
  const sv = d.site_verification;
  const roomTypes = d.concepts?.[0]?.estimate?.rooms || [];
  const seed = (sv?.rooms?.length ? sv.rooms : roomTypes.map((r) => ({ room_type: r.room_type, original_area: r.approximate_area || "", verified_length: "", verified_width: "", notes: "" })));
  const [rooms, setRooms] = useState(seed);
  const [notes, setNotes] = useState(sv?.general_notes || "");
  const completed = sv?.status === "Completed";

  const save = async () => {
    const payload = {
      general_notes: notes,
      rooms: rooms.map((r) => ({
        room_type: r.room_type,
        original_area: Number(r.original_area) || null,
        verified_length: Number(r.verified_length) || null,
        verified_width: Number(r.verified_width) || null,
        verified_area: Number(r.verified_area) || null,
        notes: r.notes || "",
      })),
    };
    try { await api.post(`/designer/projects/${pid}/site-verification`, payload); toast.success("Saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const complete = async () => { if (!sv) { toast.error("Save first"); return; } try { const { data } = await api.post(`/designer/site-verifications/${sv.id}/complete`); toast.success(data.mismatch_rooms?.length ? `Completed — ${data.mismatch_rooms.length} room(s) differ` : "Verified — all rooms match"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); } };
  const set = (i, k, val) => { const r = [...rooms]; r[i] = { ...r[i], [k]: val }; setRooms(r); };

  return (
    <div className="space-y-4" data-testid="site-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-neutral-900 flex items-center gap-2"><Ruler size={17} className="text-[#F56B1F]" /> Site Verification</h3>
        {sv && <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${completed ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{sv.status}</span>}
      </div>
      <p className="text-xs text-neutral-500">Compare the original (planned) area with the on-site verified measurement. Rooms varying more than 5% are flagged.</p>
      <div className="bg-white border border-[#E3DAC8] rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#F0EBE1] text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">
          <div className="col-span-3">Room</div><div className="col-span-2">Original sqft</div><div className="col-span-2">Ver. L (ft)</div><div className="col-span-2">Ver. W (ft)</div><div className="col-span-3">Variance</div>
        </div>
        {rooms.map((r, i) => {
          const oa = Number(r.original_area) || 0;
          const va = (Number(r.verified_length) || 0) * (Number(r.verified_width) || 0) || Number(r.verified_area) || 0;
          const variance = oa ? Math.round(Math.abs(va - oa) / oa * 1000) / 10 : 0;
          const mismatch = oa && va && variance > 5;
          return (
            <div key={i} data-testid={`site-room-${i}`} className="grid grid-cols-12 gap-2 px-4 py-2 items-center border-t border-[#F0EBE1]">
              <input disabled={completed} value={r.room_type} onChange={(e) => set(i, "room_type", e.target.value)} className="col-span-3 rounded-lg border border-[#E3DAC8] px-2 py-1.5 text-sm disabled:bg-neutral-50" />
              <input disabled={completed} type="number" value={r.original_area} onChange={(e) => set(i, "original_area", e.target.value)} className="col-span-2 rounded-lg border border-[#E3DAC8] px-2 py-1.5 text-sm mono disabled:bg-neutral-50" />
              <input disabled={completed} type="number" value={r.verified_length || ""} onChange={(e) => set(i, "verified_length", e.target.value)} className="col-span-2 rounded-lg border border-[#E3DAC8] px-2 py-1.5 text-sm mono disabled:bg-neutral-50" />
              <input disabled={completed} type="number" value={r.verified_width || ""} onChange={(e) => set(i, "verified_width", e.target.value)} className="col-span-2 rounded-lg border border-[#E3DAC8] px-2 py-1.5 text-sm mono disabled:bg-neutral-50" />
              <div className="col-span-3 text-sm flex items-center gap-1">
                {va ? (mismatch ? <span className="text-amber-600 flex items-center gap-1 mono"><AlertTriangle size={13} /> {variance}%</span> : <span className="text-green-600 flex items-center gap-1 mono"><CheckCircle2 size={13} /> {variance}%</span>) : <span className="text-neutral-300">—</span>}
              </div>
            </div>
          );
        })}
      </div>
      <textarea disabled={completed} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="General site notes…" rows={2} className="w-full rounded-xl border border-[#E3DAC8] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F] disabled:bg-neutral-50" />
      {!completed && (
        <div className="flex gap-2">
          <button onClick={() => setRooms([...rooms, { room_type: "New room", original_area: "", verified_length: "", verified_width: "", notes: "" }])} className="rounded-xl border border-[#E3DAC8] text-sm px-3 py-2 flex items-center gap-1"><Plus size={14} /> Room</button>
          <button onClick={save} data-testid="site-save-btn" className="rounded-xl bg-[#3E3E3E] text-white text-sm font-semibold px-4 py-2">Save</button>
          <button onClick={complete} data-testid="site-complete-btn" className="rounded-xl bg-[#F56B1F] text-white text-sm font-semibold px-4 py-2 flex items-center gap-1.5"><CheckCircle2 size={15} /> Mark complete</button>
        </div>
      )}
    </div>
  );
}

function ProjectLanding({ pid, onBack }) {
  const [d, setD] = useState(null);
  const [sub, setSub] = useState("overview");
  const load = () => api.get(`/designer/projects/${pid}`).then((r) => setD(r.data)).catch(() => toast.error("Could not load project"));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  if (!d) return <div className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading project…</div>;
  const { project, customer, property, interior_profile: ip, concepts, favourite, handoff } = d;

  const Card = ({ icon: Icon, title, children }) => (
    <div className="bg-white border border-[#E3DAC8] rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-400 font-semibold mb-3"><Icon size={14} className="text-[#F56B1F]" />{title}</div>
      {children}
    </div>
  );
  const Row = ({ k, v }) => <div className="flex justify-between text-sm py-1 border-b border-[#F0EBE1] last:border-0"><span className="text-neutral-500">{k}</span><span className="text-neutral-900 font-medium text-right">{v || "—"}</span></div>;

  const tabs = [["overview", "Overview", ClipboardList], ["versions", "Design Versions", Layers], ["site", "Site Verification", Ruler]];

  return (
    <div className="space-y-5" data-testid="project-landing">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900"><ArrowLeft size={16} /> Back to workspace</button>
      <div className="bg-[#3E3E3E] text-white rounded-2xl p-6 flex items-center justify-between">
        <div>
          <div className="font-heading text-xl font-bold">{project.project_name}</div>
          <div className="text-sm text-[#c9c2b4] mono">{project.project_code} · {project.current_stage}</div>
        </div>
        {d.design_frozen && <span className="flex items-center gap-1.5 text-sm bg-[#4a4a4a] rounded-full px-3 py-1.5"><Snowflake size={14} className="text-[#7fd0ff]" /> Frozen</span>}
      </div>

      <div className="flex gap-2 border-b border-[#E3DAC8]">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setSub(key)} data-testid={`landing-tab-${key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${sub === key ? "border-[#F56B1F] text-[#D65A10]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {sub === "overview" && (
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Card icon={User} title="Customer">
              <Row k="Name" v={customer ? `${customer.first_name} ${customer.last_name || ""}` : "—"} />
              <Row k="Phone" v={customer?.phone} /><Row k="Email" v={customer?.email} /><Row k="Code" v={customer?.customer_code} />
            </Card>
            <Card icon={MapPin} title="Home / Property">
              <Row k="Type" v={property?.property_type} />
              <Row k="Built-up area" v={property?.built_up_area ? `${property.built_up_area} sqft` : "—"} />
              <Row k="Bedrooms" v={property?.bedrooms} />
              <Row k="Location" v={[property?.city, property?.state].filter(Boolean).join(", ")} />
            </Card>
            <Card icon={Palette} title="Style Preferences">
              <Row k="Primary style" v={ip?.primary_style} /><Row k="Atmosphere" v={ip?.atmosphere} />
              <Row k="Colour palette" v={ip?.colour_palette} /><Row k="Materials" v={ip?.material_direction} />
            </Card>
            <Card icon={IndianRupee} title="Budget (indicative)">
              <div className="text-3xl font-bold mono text-neutral-900">{fmtBudget(ip?.budget_range)}</div>
              <p className="text-xs text-neutral-400 mt-1">Customer-stated indicative budget. Not a commercial commitment.</p>
              {favourite?.concept_name && <div className="mt-3 text-sm"><span className="text-neutral-500">Favourite concept: </span><span className="font-semibold text-[#D65A10]">{favourite.concept_name}</span></div>}
            </Card>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-400 font-semibold mb-3"><Sparkles size={14} className="text-[#F56B1F]" />AI Design Concepts</div>
            {(!concepts || concepts.length === 0) ? <div className="text-sm text-neutral-400">No AI concepts generated.</div> : (
              <div className="grid md:grid-cols-3 gap-4" data-testid="landing-concepts">
                {concepts.map((c) => (
                  <div key={c.id} className={`bg-white rounded-2xl border overflow-hidden ${favourite?.concept_id === c.id ? "border-[#F56B1F] ring-2 ring-[#F56B1F]/30" : "border-[#E3DAC8]"}`}>
                    {c.render_url ? <img src={`${BACKEND}${c.render_url}`} alt={c.concept_name} className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-[#EEE9DE] flex items-center justify-center text-xs text-neutral-400">No visual yet</div>}
                    <div className="p-4">
                      <div className="font-heading font-bold text-neutral-900">{c.concept_name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{c.style}</div>
                      <div className="mt-2 mono text-sm font-bold text-neutral-900">{inr(c.estimated_cost_min)} – {inr(c.estimated_cost_max)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {handoff?.customer_feedback?.length > 0 && (
            <Card icon={ClipboardList} title="Customer Feedback">
              <ul className="space-y-1">{handoff.customer_feedback.map((f, i) => <li key={i} className="text-sm text-neutral-600">• {f.type}{f.comment ? ` — ${f.comment}` : ""}</li>)}</ul>
            </Card>
          )}
        </div>
      )}

      {sub === "versions" && <VersionsPanel d={d} pid={pid} reload={load} />}
      {sub === "site" && <SitePanel key={d.site_verification?.id || "new"} d={d} pid={pid} reload={load} />}
    </div>
  );
}


export default function DesignerWorkspace() {
  const { can } = useAuth();
  const canAllocate = can("projects.assign");
  const [tab, setTab] = useState(canAllocate ? "allocation" : "mine");
  const [openPid, setOpenPid] = useState(null);

  return (
    <AppShell title="Designer Workspace">
      {openPid ? (
        <ProjectLanding pid={openPid} onBack={() => setOpenPid(null)} />
      ) : (
        <>
          <div className="flex gap-2 mb-6 border-b border-[#E3DAC8]">
            {canAllocate && (
              <button onClick={() => setTab("allocation")} data-testid="tab-allocation"
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "allocation" ? "border-[#F56B1F] text-[#D65A10]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
                Designer Allocation
              </button>
            )}
            <button onClick={() => setTab("mine")} data-testid="tab-my-assignments"
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "mine" ? "border-[#F56B1F] text-[#D65A10]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
              My Assignments
            </button>
          </div>
          {tab === "allocation" && canAllocate ? <Allocation onOpen={setOpenPid} /> : <MyAssignments onOpen={setOpenPid} />}
        </>
      )}
    </AppShell>
  );
}
